/**
 * Snapshot recovery service for Aegix One.
 *
 * Migrated to typed `client.GET/...` per ADR 113 §Decision.
 *
 * @see doc/adr/decisions/078-realtime-resilience-architecture.md — no client-side polling
 * @see doc/adr/decisions/106-reunification-lifecycle-decoupled-from-alert-lifecycle.md — reunification lifecycle decoupled from alert
 * @see apps/aegix-aim-backend/server/api/v2/snapshot_views.py
 */

import { client, unwrap } from '../lib/http-client';
import type {
  ReunificationEventSummary,
  StudentReunification,
} from './reunification';

export interface SnapshotAlertHistory {
  alert_id?: number | null;
  school?: number | null;
  initiator?: number | null;
  canceller?: number | null;
  cancelled?: string | null;
  latest?: boolean;
  [key: string]: unknown;
}

export interface SnapshotRoomStatus {
  alert_id?: number | null;
  school?: number | null;
  [key: string]: unknown;
}

export interface SnapshotGuardianReunification {
  public_id: string;
  event_id?: string;
  status?: string;
  baseuser?: number | null;
  [key: string]: unknown;
}

export interface SnapshotEventStaff {
  public_id: string;
  event_id?: string;
  [key: string]: unknown;
}

export interface SnapshotPayload {
  active_alerts: SnapshotAlertHistory[];
  room_statuses: SnapshotRoomStatus[];
  reunification_event: ReunificationEventSummary | null;
  reunification_students: StudentReunification[];
  reunification_guardians: SnapshotGuardianReunification[];
  reunification_staff: SnapshotEventStaff[];
  server_ts: string;
}

/**
 * Fetch the unified recovery snapshot.
 */
export async function fetchSnapshot(
  schoolId?: string | number,
): Promise<SnapshotPayload> {
  const result = await client.GET('/api/v2/alerts/snapshot/', {
    params: {
      query:
        schoolId !== undefined && schoolId !== null && schoolId !== ''
          ? ({ school_id: String(schoolId) } as never)
          : ({} as never),
    },
  });
  return unwrap(result) as unknown as SnapshotPayload;
}
